import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  confirmTierUpgrade,
  createTierCheckout,
  getTierCatalog,
  getTierSummary,
  scheduleTierDowngrade,
  type BillingIntervalKey,
  type TierKey,
} from './api'

/**
 * Tier + metered-usage summary backing the sidebar TierWidget and the
 * /upgrade pages. Refetches on a 60s interval so the free-tier usage bar
 * tracks the worker-side meter (60s cache TTL) without a manual refresh.
 */
export function useTierSummary(accountId: string) {
  return useQuery({
    queryKey: ['tier-summary', accountId],
    queryFn: () => getTierSummary(accountId),
    enabled: !!accountId,
    refetchInterval: 60_000,
  })
}

/**
 * Runtime tier catalog (prices/features/inclusions from the admin Tiers
 * console) for /upgrade, checkout, and the upgrade modal. Config edits land
 * within the API's 30s cache; a page refetch on mount is fresh enough.
 */
export function useTierCatalog(accountId: string | null | undefined) {
  return useQuery({
    queryKey: ['tier-catalog', accountId],
    queryFn: () => getTierCatalog(accountId!),
    enabled: !!accountId,
    staleTime: 60_000,
  })
}

// No `useInvoicingEnabled` / `useInvoicingUpgradeTier` here by decision:
// (2026-08-19) removed the invoicing tier gate outright, so there is no locked
// create-invoice control left to pitch an upgrade from. A Growth account can
// always invoice; Growth is monetized by the per-invoice fee, which
// meters rather than gates. Do not reintroduce either hook.
const TIER_RANK_ORDER: TierKey[] = ['GROWTH', 'PRO', 'MAX']

/**
 * Cheapest tier ABOVE the account's current one whose runtime config includes
 * the full voice library (`all_voices`) — aims the locked voice-library rows'
 * lock pill, upsell copy, and UpgradeModal pitch on `/settings/phone-assistant`
 * (#526). Falls back to PRO (the bundled `all_voices` tier) while the catalog
 * loads, so the default render is unchanged — the fallback picks which tier to
 * PITCH, never what to call it (naming is `useTierLabel`'s job). The
 * server independently 403s TIER_REQUIRED — this only picks the pitch.
 */
export function useVoiceLibraryUpgradeTier(
  accountId: string | null | undefined,
): 'PRO' | 'MAX' {
  const catalog = useTierCatalog(accountId)
  const { data: summary } = useTierSummary(accountId ?? '')
  const tiers = catalog.data?.tiers ?? []
  const currentRank = summary ? TIER_RANK_ORDER.indexOf(summary.tier) : -1
  const unlock =
    tiers.find(
      (t) => TIER_RANK_ORDER.indexOf(t.tier) > currentRank && t.features.all_voices === true,
    )?.tier ??
    tiers.find((t) => t.features.all_voices === true)?.tier ??
    'PRO'
  return unlock === 'MAX' ? 'MAX' : 'PRO'
}

/**
 * Display name for a tier — the runtime catalog's `label`, or `undefined` when
 * the catalog has not resolved one (still loading, errored, or serving a
 * catalog this tier is missing from). There is deliberately NO constant
 * fallback: makes the runtime catalog the only authority on tier identity
 * (as did for the tier list itself), so `SUBSCRIPTION_TIER_LABELS` is
 * never the source of a name a contractor reads. Callers render tier-NEUTRAL
 * copy on `undefined` rather than guessing a plan word — guessing is how
 * "Concierge" (now a separate product line) kept leaking back in (#565).
 */
export function useTierLabel(
  accountId: string | null | undefined,
  tier: TierKey,
): string | undefined {
  const catalog = useTierCatalog(accountId)
  return catalog.data?.tiers.find((t) => t.tier === tier)?.label
}

/**
 * Team group chats (plans/team-chat-and-meetings.md): has the account used up
 * its tier's `maxGroupChats`, and which tier unlocks more? Numeric limits are
 * the newer entitlement shape, so this can't reuse `useVoiceLibraryUpgradeTier`'s
 * boolean-feature lookup.
 *
 * FAIL-OPEN while the catalog loads or when an older API omits the field
 * (`undefined` ≠ a limit of 0): the New-group-chat trigger stays live and the
 * server's fail-closed 403 `TIER_REQUIRED` remains the authority — the caller
 * opens the same UpgradeModal on that response.
 */
export function useGroupChatUpgradeTier(
  accountId: string | null | undefined,
  currentCount: number,
): { limitReached: boolean; requiredTier: 'PRO' | 'MAX' } {
  const catalog = useTierCatalog(accountId)
  const { data: summary } = useTierSummary(accountId ?? '')
  const tiers = catalog.data?.tiers ?? []
  const currentRank = summary ? TIER_RANK_ORDER.indexOf(summary.tier) : -1
  const mine = summary ? tiers.find((t) => t.tier === summary.tier) : undefined
  const limit = mine?.maxGroupChats
  const limitReached = typeof limit === 'number' && currentCount >= limit
  // The cheapest tier above the current one that raises (or removes) the cap.
  const unlock =
    tiers.find(
      (t) =>
        TIER_RANK_ORDER.indexOf(t.tier) > currentRank &&
        (t.maxGroupChats === null ||
          (typeof t.maxGroupChats === 'number' &&
            typeof limit === 'number' &&
            t.maxGroupChats > limit)),
    )?.tier ?? (currentRank >= TIER_RANK_ORDER.indexOf('PRO') ? 'MAX' : 'PRO')
  return { limitReached, requiredTier: unlock === 'MAX' ? 'MAX' : 'PRO' }
}

/**
 * Meeting transcripts + AI summaries (plans/team-chat-and-meetings.md): which
 * tier raises the account's `monthlyAiMeetings` cap? Aims the UpgradeModal on
 * `MeetingLockedPanel`, which shows when the worker denied a quota slot and
 * stamped `summaryStatus = 'SKIPPED_QUOTA'`.
 *
 * Same numeric-limit lookup as `useGroupChatUpgradeTier` — a tier qualifies
 * when its cap is null (unlimited) or strictly higher than the current one.
 * Unlike that hook there is no fail-open decision to make: the lock is a fact
 * the server already recorded on the meeting, so this only picks the pitch.
 */
export function useMeetingAiUpgradeTier(
  accountId: string | null | undefined,
): 'PRO' | 'MAX' {
  const catalog = useTierCatalog(accountId)
  const { data: summary } = useTierSummary(accountId ?? '')
  const tiers = catalog.data?.tiers ?? []
  const currentRank = summary ? TIER_RANK_ORDER.indexOf(summary.tier) : -1
  const limit = summary ? tiers.find((t) => t.tier === summary.tier)?.monthlyAiMeetings : undefined
  const unlock =
    tiers.find(
      (t) =>
        TIER_RANK_ORDER.indexOf(t.tier) > currentRank &&
        (t.monthlyAiMeetings === null ||
          (typeof t.monthlyAiMeetings === 'number' &&
            typeof limit === 'number' &&
            t.monthlyAiMeetings > limit)),
    )?.tier ?? (currentRank >= TIER_RANK_ORDER.indexOf('PRO') ? 'MAX' : 'PRO')
  return unlock === 'MAX' ? 'MAX' : 'PRO'
}

/**
 * Cheapest PAID tier above the account's current one. Aims the UpgradeModal for
 * pitches that aren't keyed on a runtime feature flag — multi-organization isn't
 * in the feature matrix (it isn't built), so `useVoiceLibraryUpgradeTier`'s
 * catalog lookup has nothing to match on. Clamped to PRO/MAX because
 * the modal only knows the paid tiers.
 */
export function useNextPaidTier(
  accountId: string | null | undefined,
): 'PRO' | 'MAX' {
  const { data } = useTierSummary(accountId ?? '')
  const currentRank = data ? TIER_RANK_ORDER.indexOf(data.tier) : -1
  return currentRank >= TIER_RANK_ORDER.indexOf('PRO') ? 'MAX' : 'PRO'
}

/** Checkout bootstrap: quote + (when no card on file) a SetupIntent secret.
 *  The cadence rides along because the quote differs — an annual purchase is
 *  charged a year up front, credited for what the current plan already cost. */
export function useTierCheckout(accountId: string) {
  return useMutation({
    mutationFn: (v: {
      tier: Extract<TierKey, 'PRO' | 'MAX'>
      interval?: BillingIntervalKey
    }) => createTierCheckout(accountId, v.tier, v.interval ?? 'MONTHLY'),
  })
}

export function useConfirmUpgrade(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: {
      tier: Extract<TierKey, 'PRO' | 'MAX'>
      interval?: BillingIntervalKey
    }) => confirmTierUpgrade(accountId, v.tier, v.interval ?? 'MONTHLY'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tier-summary', accountId] })
      qc.invalidateQueries({ queryKey: ['account', accountId] })
    },
  })
}

/** Schedules a step down OR a same-tier yearly → monthly cadence change, which
 *  is why `tier` isn't clamped to the lower tiers any more. */
export function useDowngrade(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { tier: TierKey; interval?: BillingIntervalKey }) =>
      scheduleTierDowngrade(accountId, v.tier, v.interval),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tier-summary', accountId] })
    },
  })
}
